import Bugsnag from "@bugsnag/js"
import { isFetchError, isLocalStorageError } from "./errorsHelpers"
import { ERROR_PIPE_META_DATA, ERROR_SOURCE_ADAPTER_NAME, errorsManager } from "./errorsManager"

export const bugsnagAdapter = {
  // Let errors manager recognize the adapter
  adapterName: "bugsnagAdapter",

  init() {
    const API_KEY = "a383d74fed09040a8180a6388ef025f7"

    // Start Bugsnag with global configuration
    Bugsnag.start({
      apiKey: API_KEY,
      // The `onError` processes all kinds of errors caught via Bugsnag
      // both caught automatically and handled explicitly
      onError: (event) => {
        const userEmail = event.originalError[ERROR_PIPE_META_DATA]?.email
        if (userEmail) {
          event.setUser(undefined, userEmail, undefined)
        }

        // If not an Error object has bee thrown - catch it and process to error
        // to avoid tons of vague `nhandledrejection handler received a non-error` events
        if (!(event.originalError instanceof Error)) {
          const convertedToError = new Error(`Non-Error rejection`)
          convertedToError[ERROR_SOURCE_ADAPTER_NAME] = this.adapterName
          // Keep original error for future parsing in report handler
          convertedToError.originalError = event.originalError

          errorsManager.reportNonError(convertedToError)
          return false
        }

        // Catch unprocessed localStorage errors, which leak from non-handled places
        // like 3rd party scripts and delegate them to the errorManager to process centrally
        if (
          isLocalStorageError(event.originalError) &&
          !event.originalError[ERROR_PIPE_META_DATA]
        ) {
          errorsManager.reportLocalStorageError(event.originalError)
          return false
        }

        // Intercept all fetch errors in one single place centrally
        if (isFetchError(event.originalError)) {
          // Process caught intercepted fetch error with specific handler
          errorsManager.reportFetchError(event.originalError)

          // Discard original event, cause we already sent specific processed one above
          return false
        }
      },
    })

    window.Bugsnag = Bugsnag
  },

  reportLocalStorageError(normalizedError) {
    if (!normalizedError) return

    Bugsnag.notify(normalizedError, (event) => {
      // Normalize Bugsnag event fields to let the dashboard show our custom data
      event.unhandled = false
      event.severity = "warning"

      // The `context` is used for grouping and also shown on UI near the bold title
      event.context = "Central localStorage errors report"

      // Force grouping all LS related errors to 1 group
      event.groupingHash = "[CPPV3] localStorage"

      // The `errorClass` used for error bold title on UI
      event.errors[0].errorClass = `[CPPV3] localStorage Error`
      // The `errorMessage` used for subtitle on UI
      event.errors[0].errorMessage = normalizedError.message

      if (normalizedError) {
        event.addMetadata("PIPE METADATA", normalizedError[ERROR_PIPE_META_DATA])
      }
    })
  },

  reportFetchError(normalizedError) {
    if (!normalizedError) return

    Bugsnag.notify(normalizedError, (event) => {
      const caller = window.location.hostname + window.location.pathname
      const method = normalizedError[ERROR_PIPE_META_DATA]?.method
      const url = normalizedError[ERROR_PIPE_META_DATA]?.url
      const urlObj = new URL(url)
      const urlOrigin = urlObj.origin

      // Normalize Bugsnag event fields to let the dashboard show our custom data
      // The `context` is shown on UI near the bold title
      event.context = `Request from "${caller}" failed`

      // Force grouping for consistent Bugsnag dashboard, as depending on browser
      // same errors may have different messages and other data fields.
      // The grouping criteria is a groupingHash value match for different errors:
      event.groupingHash = `[CPPV3] ${caller} -> ${method}: ${urlOrigin}`

      // The `errorClass` used for error bold title on UI
      event.errors[0].errorClass = `[CPPV3] FETCH Error`
      // The `errorMessage` used for subtitle on UI
      event.errors[0].errorMessage = normalizedError.message

      if (normalizedError) {
        const pipeMetaData = normalizedError[ERROR_PIPE_META_DATA]
        // Cleanup unneeded props
        delete pipeMetaData.method
        delete pipeMetaData.url

        event.addMetadata("PIPE METADATA", pipeMetaData)
      }
    })
  },

  reportError(error) {
    if (!error) return

    Bugsnag.notify(error, (event) => {
      if (error) {
        event.addMetadata("PIPE METADATA", error[ERROR_PIPE_META_DATA])
      }
    })
  },

  reportNonError(error) {
    if (!error) return

    Bugsnag.notify(error, (event) => {
      // Get the last breadcrumb with failed request - may be it will shed some light on the error reason
      const failedRequestBreadcrumb = event.breadcrumbs?.findLast(
        (b) => b.message === "XMLHttpRequest error",
      )
      const failedRequestInfo = failedRequestBreadcrumb?.metadata?.request

      event.groupingHash = `[CPPV3] non-error`
      event.errors[0].errorClass = `[CPPV3] A non-error object reported`
      event.errors[0].errorMessage = `${error.message}. Last failed request: ${failedRequestInfo}`

      if (error) {
        event.addMetadata("PIPE METADATA", error[ERROR_PIPE_META_DATA])
      }
    })
  },

  reportAlpineError(normalizedError) {
    if (!normalizedError) return

    Bugsnag.notify(normalizedError, (event) => {
      event.groupingHash = "[CPPV3] Alpine error"
      event.errors[0].errorClass = "[CPPV3] Alpine Error"
      event.errors[0].errorMessage = normalizedError.message

      if (normalizedError) {
        event.addMetadata("PIPE METADATA", normalizedError[ERROR_PIPE_META_DATA])
      }
    })
  },
}
